import { useTranslation } from 'react-i18next';

// Default timezone: Europe/Paris as per user requirements
const DEFAULT_TIMEZONE = 'Europe/Paris';

/**
 * Locale-aware number formatting
 */
export const formatNumber = (
  value: number,
  locale: string,
  options?: Intl.NumberFormatOptions
): string => {
  return new Intl.NumberFormat(locale, options).format(value);
};

/**
 * Locale-aware date formatting
 */
export const formatDate = (
  date: Date | string | number,
  locale: string,
  options?: Intl.DateTimeFormatOptions
): string => {
  const dateObj = typeof date === 'string' || typeof date === 'number' ? new Date(date) : date;
  
  const defaultOptions: Intl.DateTimeFormatOptions = {
    timeZone: DEFAULT_TIMEZONE,
    ...options,
  };

  return new Intl.DateTimeFormat(locale, defaultOptions).format(dateObj);
};

/**
 * Locale-aware time formatting with 24h clock for fr/es/it/ar, locale default for en
 */
export const formatTime = (
  date: Date | string | number,
  locale: string,
  options?: Intl.DateTimeFormatOptions
): string => {
  const dateObj = typeof date === 'string' || typeof date === 'number' ? new Date(date) : date;
  
  // Default to 24h for fr/es/it/ar, locale default for en
  const hour12 = locale === 'en' ? undefined : false;
  
  const defaultOptions: Intl.DateTimeFormatOptions = {
    timeZone: DEFAULT_TIMEZONE,
    hour: '2-digit',
    minute: '2-digit',
    hour12,
    ...options,
  };

  return new Intl.DateTimeFormat(locale, defaultOptions).format(dateObj);
};

/**
 * Locale-aware datetime formatting
 */
export const formatDateTime = (
  date: Date | string | number,
  locale: string,
  options?: Intl.DateTimeFormatOptions
): string => {
  const dateObj = typeof date === 'string' || typeof date === 'number' ? new Date(date) : date;
  
  const hour12 = locale === 'en' ? undefined : false;
  
  const defaultOptions: Intl.DateTimeFormatOptions = {
    timeZone: DEFAULT_TIMEZONE,
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12,
    ...options,
  };

  return new Intl.DateTimeFormat(locale, defaultOptions).format(dateObj);
};

/**
 * Locale-aware percentage formatting
 */
export const formatPercentage = (
  value: number,
  locale: string,
  decimals: number = 1
): string => {
  return new Intl.NumberFormat(locale, {
    style: 'percent',
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(value / 100);
};

/**
 * Locale-aware currency formatting
 */
export const formatCurrency = (
  value: number,
  locale: string,
  currency: string = 'EUR'
): string => {
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
  }).format(value);
};

/**
 * Unit formatting with locale-aware numbers
 * Keeps units as-is (m³, bar, mS/cm) as per user requirements
 */
export const formatUnit = (
  value: number,
  unit: string,
  locale: string,
  decimals?: number
): string => {
  const formattedNumber = new Intl.NumberFormat(locale, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(value);
  
  return `${formattedNumber} ${unit}`;
};

/**
 * CSV separator based on locale
 * ; for fr/es/it/ar, , for en as per user requirements
 */
export const getCSVSeparator = (locale: string): string => {
  return locale === 'en' ? ',' : ';';
};

/**
 * Decimal separator based on locale
 */
export const getDecimalSeparator = (locale: string): string => {
  const formatted = new Intl.NumberFormat(locale).format(1.1);
  return formatted.charAt(1);
};

/**
 * Thousands separator based on locale
 */
export const getThousandsSeparator = (locale: string): string => {
  const formatted = new Intl.NumberFormat(locale).format(1000);
  return formatted.charAt(1);
};

/**
 * Hook for locale-aware formatting utilities
 */
export const useIntlFormatting = () => {
  const { i18n } = useTranslation();
  const locale = i18n.language;

  return {
    formatNumber: (value: number, options?: Intl.NumberFormatOptions) => 
      formatNumber(value, locale, options),
    formatDate: (date: Date | string | number, options?: Intl.DateTimeFormatOptions) => 
      formatDate(date, locale, options),
    formatTime: (date: Date | string | number, options?: Intl.DateTimeFormatOptions) => 
      formatTime(date, locale, options),
    formatDateTime: (date: Date | string | number, options?: Intl.DateTimeFormatOptions) => 
      formatDateTime(date, locale, options),
    formatPercentage: (value: number, decimals?: number) => 
      formatPercentage(value, locale, decimals),
    formatCurrency: (value: number, currency?: string) => 
      formatCurrency(value, locale, currency),
    formatUnit: (value: number, unit: string, decimals?: number) => 
      formatUnit(value, unit, locale, decimals),
    getCSVSeparator: () => getCSVSeparator(locale),
    getDecimalSeparator: () => getDecimalSeparator(locale),
    getThousandsSeparator: () => getThousandsSeparator(locale),
    locale,
  };
};

/**
 * Format relative time (e.g., "2 hours ago")
 */
export const formatRelativeTime = (
  date: Date | string | number,
  locale: string
): string => {
  const dateObj = typeof date === 'string' || typeof date === 'number' ? new Date(date) : date;
  const now = new Date();
  const diffInSeconds = Math.floor((now.getTime() - dateObj.getTime()) / 1000);

  const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });

  if (diffInSeconds < 60) {
    return rtf.format(-diffInSeconds, 'second');
  } else if (diffInSeconds < 3600) {
    return rtf.format(-Math.floor(diffInSeconds / 60), 'minute');
  } else if (diffInSeconds < 86400) {
    return rtf.format(-Math.floor(diffInSeconds / 3600), 'hour');
  } else if (diffInSeconds < 2592000) {
    return rtf.format(-Math.floor(diffInSeconds / 86400), 'day');
  } else if (diffInSeconds < 31536000) {
    return rtf.format(-Math.floor(diffInSeconds / 2592000), 'month');
  } else {
    return rtf.format(-Math.floor(diffInSeconds / 31536000), 'year');
  }
};